import React, { useState, useRef, useEffect } from "react";
import { useDroppable } from "@dnd-kit/core";
import KanbanCard from "./KanbanCard";
import { Trophy, XCircle, GripVertical, MoreVertical, Trash2, Check, Pencil } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const COLORS = ["#25D366", "#3B82F6", "#8B5CF6", "#06B6D4", "#EC4899", "#F97316", "#64748B", "#EF4444"];

export default function KanbanColumn({ column, leads, onCardClick, onColumnUpdate, onColumnDelete, dragHandle }) {
  const { isOver, setNodeRef } = useDroppable({ id: column.id, data: { column } });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(column.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const saveName = async () => {
    const n = name.trim();
    if (!n || n === column.name) {
      setEditing(false);
      setName(column.name);
      return;
    }
    try {
      const { data } = await api.patch(`/columns/${column.id}`, { name: n });
      onColumnUpdate?.(column.id, data);
      toast.success("Etapa renomeada");
    } catch {
      toast.error("Erro ao renomear");
    }
    setEditing(false);
  };

  const changeColor = async (color) => {
    try {
      const { data } = await api.patch(`/columns/${column.id}`, { color });
      onColumnUpdate?.(column.id, data);
    } catch {}
    setMenuOpen(false);
  };

  const remove = async () => {
    if (column.is_system) {
      toast.error(`O funil "${column.name}" é obrigatório e não pode ser excluído. Você pode renomeá-lo.`);
      setMenuOpen(false);
      return;
    }
    if (!window.confirm(`Excluir a etapa "${column.name}"? Leads serão movidos para a primeira coluna.`)) return;
    try {
      await api.delete(`/columns/${column.id}`);
      onColumnDelete?.(column.id);
      toast.info("Etapa excluída");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
    setMenuOpen(false);
  };

  const total = leads.reduce((s, l) => s + (Number(l.value) || 0), 0);

  return (
    <div
      data-testid={`kanban-column-${column.id}`}
      className="w-80 min-w-[20rem] max-w-[20rem] flex flex-col gap-3 shrink-0"
    >
      <div
        data-column-header
        className="flex items-center justify-between px-2 py-2.5 rounded-lg border group"
        style={{ backgroundColor: `${column.color}12`, borderColor: `${column.color}45` }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (!editing) setEditing(true);
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* drag handle */}
          <button
            {...(dragHandle || {})}
            data-testid={`column-drag-${column.id}`}
            title="Arrastar coluna"
            className="p-1 text-slate-500 hover:text-slate-200 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition"
          >
            <GripVertical className="w-4 h-4" />
          </button>

          {column.is_won ? (
            <Trophy className="w-4 h-4 text-[#25D366] shrink-0" />
          ) : column.is_lost ? (
            <XCircle className="w-4 h-4 text-red-400 shrink-0" />
          ) : (
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: column.color }} />
          )}

          {editing ? (
            <input
              ref={inputRef}
              data-testid={`column-name-input-${column.id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveName();
                if (e.key === "Escape") { setName(column.name); setEditing(false); }
              }}
              className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-0.5 text-sm text-slate-100 focus:outline-none focus:border-[#25D366]"
            />
          ) : (
            <h3
              className="text-sm font-bold text-slate-100 truncate flex-1"
              style={{ fontFamily: "Outfit" }}
              title="Duplo clique para editar"
            >
              {column.name}
            </h3>
          )}

          <span className="text-[11px] font-semibold text-slate-400 bg-slate-900/80 px-1.5 py-0.5 rounded-md shrink-0">
            {leads.length}
          </span>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            data-testid={`column-menu-${column.id}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen((x) => !x); }}
            className="p-1 text-slate-500 hover:text-slate-200 opacity-0 group-hover:opacity-100 transition"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-20 p-2" data-testid={`column-menu-panel-${column.id}`}>
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(true); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-slate-300 hover:bg-slate-800 rounded-md"
              >
                <Pencil className="w-3.5 h-3.5" /> Renomear
              </button>
              <div className="px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Cor</div>
                <div className="flex items-center gap-1 flex-wrap">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      data-testid={`column-color-${column.id}-${c}`}
                      onClick={() => changeColor(c)}
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: c,
                        boxShadow: column.color === c ? `0 0 0 2px #0f172a, 0 0 0 4px ${c}` : "none",
                      }}
                    >
                      {column.color === c && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-800 my-1" />
              {column.is_system ? (
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-600 cursor-not-allowed" title="Funil obrigatório do sistema">
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                  <span className="ml-auto text-[9px] font-semibold uppercase tracking-wider text-slate-700">fixo</span>
                </div>
              ) : (
                <button
                  data-testid={`column-delete-${column.id}`}
                  onClick={remove}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-md"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                </button>
              )}
            </div>
          )}
        </div>

        {total > 0 && !editing && (
          <span className="text-[11px] font-bold text-slate-300 ml-1 shrink-0">
            R$ {total.toLocaleString("pt-BR")}
          </span>
        )}
      </div>

      <div
        ref={setNodeRef}
        className={`flex-1 flex flex-col gap-2 p-2 rounded-xl border-2 border-dashed transition-colors min-h-[200px] ${
          isOver ? "border-[#25D366]/50 bg-[#25D366]/5" : "border-transparent bg-slate-800/20"
        }`}
      >
        {leads.map((lead, i) => (
          <KanbanCard key={lead.id} lead={lead} index={i} onClick={onCardClick} />
        ))}
        {leads.length === 0 && (
          <div className="text-center py-8 text-xs text-slate-600">Solte um lead aqui</div>
        )}
      </div>
    </div>
  );
}
